import cn from 'classnames';
import { ChangeEvent, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { Button } from '@Components/Button';
import { Appearance, Design } from '@Components/designConstants';
import { useSetError } from '@src/redux/modals';
import { Mixpanel, MxE } from '@src/utils/mixpanelWrapper';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { InstanceInput, ModalMode } from '../amc-instances-types';
import { modeLabel, modeNote, writeSuccess } from '../amc-instances-utils';
import { useCreateInstance, useUpdateInstance } from '../utils/useAmcInstances';
import { AdvertiserManager } from './AdvertiserManager';

const EMPTY_FORM: InstanceInput = {
  instanceName: '',
  advertiserName: '',
  awsAccountId: '',
  s3BucketName: ''
};

interface InstanceModalProps {
  mode: ModalMode;
  live: boolean;
  onClose: () => void;
}

export const InstanceModal = ({ mode, live, onClose }: InstanceModalProps) => {
  const isEdit = mode.kind === 'edit';
  const [form, setForm] = useState<InstanceInput>(
    mode.kind === 'edit'
      ? {
          instanceName: mode.instance.instanceName ?? '',
          advertiserName: mode.instance.advertiserName ?? '',
          awsAccountId: mode.instance.awsAccountId ?? '',
          s3BucketName: mode.instance.s3BucketName ?? ''
        }
      : EMPTY_FORM
  );

  const createInstance = useCreateInstance();
  const updateInstance = useUpdateInstance();
  const setError = useSetError();
  const saving = createInstance.isPending || updateInstance.isPending;

  const setField = (key: keyof InstanceInput) => (e: ChangeEvent<HTMLInputElement>) =>
    setForm(prev => ({ ...prev, [key]: e.target.value }));

  const submit = async () => {
    try {
      if (mode.kind === 'edit') {
        const { instanceName, ...editable } = form;
        await updateInstance.mutateAsync({
          instanceId: mode.instance.instanceId,
          input: editable,
          dryRun: !live
        });
        if (live) {
          Mixpanel.track(MxE.RecordSaved, {
            page: PAGE_NAMES.AMC_INSTANCES,
            entity: 'amc_instance'
          });
        }
        setError(writeSuccess(live, `Update “${instanceName}”`));
      } else {
        await createInstance.mutateAsync({ input: form, dryRun: !live });
        if (live) {
          Mixpanel.track(MxE.RecordCreated, {
            page: PAGE_NAMES.AMC_INSTANCES,
            entity: 'amc_instance'
          });
        }
        setError(
          writeSuccess(
            live,
            `Create “${form.instanceName}”`,
            'Provisioning can take a few minutes to show up.'
          )
        );
      }
      onClose();
    } catch (e) {
      setError({
        message: `Couldn't ${isEdit ? 'update' : 'create'} the instance: ${e.message}`,
        reportError: e
      });
    }
  };

  return (
    <Modal
      show
      centered
      size='lg'
      onHide={onClose}
      dialogClassName='amc-instance-modal'
      data-testid='amc-instance-modal'
    >
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? 'Edit AMC Instance' : 'Create AMC Instance'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className={cn('amc-mode-note', { live })} data-testid='amc-mode-note'>
          {modeNote(live)}
        </div>

        <Form.Group className='amc-field'>
          <Form.Label>
            Instance name <span className='amc-required'>*</span>
          </Form.Label>
          <Form.Control
            data-testid='amc-instance-name-input'
            value={form.instanceName}
            onChange={setField('instanceName')}
            placeholder='e.g. Acme US'
            disabled={isEdit}
          />
          <div className='amc-help'>
            {isEdit
              ? 'Amazon treats the instance name as immutable after creation.'
              : 'Required. 3–50 characters; letters, numbers, and spaces only.'}
          </div>
        </Form.Group>

        <Form.Group className='amc-field'>
          <Form.Label>
            Advertiser name <span className='amc-optional'>(optional)</span>
          </Form.Label>
          <Form.Control
            data-testid='amc-advertiser-name-input'
            value={form.advertiserName}
            onChange={setField('advertiserName')}
          />
        </Form.Group>

        <Form.Group className='amc-field'>
          <Form.Label>
            AWS account id <span className='amc-optional'>(optional)</span>
          </Form.Label>
          <Form.Control value={form.awsAccountId} onChange={setField('awsAccountId')} />
          <div className='amc-help'>
            Only needed if you’ll upload your own data. Can be added later.
          </div>
        </Form.Group>

        <Form.Group className='amc-field'>
          <Form.Label>
            S3 bucket name <span className='amc-optional'>(optional)</span>
          </Form.Label>
          <Form.Control value={form.s3BucketName} onChange={setField('s3BucketName')} />
          <div className='amc-help'>
            Leave blank and Amazon provisions the reporting bucket for you.
          </div>
        </Form.Group>

        {mode.kind === 'edit' && <AdvertiserManager instance={mode.instance} live={live} />}
      </Modal.Body>
      <Modal.Footer>
        <Button appearance={Appearance.EMPTY} design={Design.NEUTRAL} onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={!form.instanceName.trim()} loading={saving} onClick={submit}>
          {modeLabel(isEdit ? 'Save' : 'Create', live)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
